import React, { useState, useEffect, useCallback } from "react";

// Styles
import { makeStyles } from "@material-ui/core/styles";

// Routing
import { withRouter } from "react-router-dom";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { fetchDashboard } from "../../redux/features/dashboard/dashboardSlice";

// Components
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";

import DashboardFilters from "./Dashboard/DashboardFilters";
import KpiTile from "./Dashboard/KpiTile";
import SalesTrendChart from "./Dashboard/SalesTrendChart";
import BreakdownCard from "./Dashboard/BreakdownCard";
import PayoutsSection from "./Dashboard/PayoutsSection";
import UpcomingArrivals from "./Dashboard/UpcomingArrivals";
import CustomersSection from "./Dashboard/CustomersSection";
import FullContainerProgress from "../UI/FullContainerProgress";
import {
  DATE_FORMAT,
  formatCurrency,
  formatDate,
  formatNumber,
  presetRange,
} from "./Dashboard/dashboardUtils";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
  },
  loadingPaper: {
    position: "relative",
    minHeight: 300,
  },
  progress: {
    height: 2,
  },
}));

const DEFAULT_PRESET = "12m";

const SectionTitle = ({ children, subtitle }) => {
  const classes = useStyles();
  return (
    <Grid item xs={12} className={classes.sectionTitle}>
      <Typography variant="h6" component="h2">
        {children}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="textSecondary">
          {subtitle}
        </Typography>
      )}
    </Grid>
  );
};

const SalesDashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { data, isLoading, error } = useSelector((state) => state.dashboard);

  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [range, setRange] = useState(() => presetRange(DEFAULT_PRESET));
  const [type, setType] = useState("all");

  const load = useCallback(() => {
    dispatch(
      fetchDashboard({
        from: range.from.format(DATE_FORMAT),
        to: range.to.format(DATE_FORMAT),
        type,
      })
    );
  }, [dispatch, range, type]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePresetChange = (key) => {
    setPreset(key);
    if (key !== "custom") setRange(presetRange(key));
  };

  const filters = (
    <DashboardFilters
      preset={preset}
      from={range.from}
      to={range.to}
      type={type}
      isLoading={isLoading}
      onPresetChange={handlePresetChange}
      onCustomRangeChange={setRange}
      onTypeChange={setType}
      onRefresh={load}
    />
  );

  const header = (
    <Grid item xs={12} className={classes.header}>
      <div>
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        {data && (
          <Typography variant="body2" color="textSecondary">
            {`${formatDate(data.range.from)} – ${formatDate(
              data.range.to
            )}, compared with ${formatDate(
              data.range.previous_from
            )} – ${formatDate(data.range.previous_to)}`}
          </Typography>
        )}
      </div>
      {filters}
    </Grid>
  );

  if (!data) {
    return (
      <Grid container spacing={3}>
        {header}
        <Grid item xs={12}>
          <Paper className={classes.loadingPaper}>
            {error ? (
              <MuiAlert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={load}>
                    Retry
                  </Button>
                }
              >
                {error.errorMessage}
              </MuiAlert>
            ) : (
              <FullContainerProgress label="Loading dashboard…" />
            )}
          </Paper>
        </Grid>
      </Grid>
    );
  }

  const { summary, range: dataRange } = data;
  const { current, previous } = summary;

  return (
    <Grid container spacing={2}>
      {header}
      <Grid item xs={12}>
        <LinearProgress
          className={classes.progress}
          style={{ visibility: isLoading ? "visible" : "hidden" }}
        />
        {error && (
          <MuiAlert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={load}>
                Retry
              </Button>
            }
          >
            {`Showing the last loaded data. ${error.errorMessage}`}
          </MuiAlert>
        )}
      </Grid>

      {/* Headline numbers */}
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiTile
          label="Booked revenue"
          value={formatCurrency(current.booked)}
          current={current.booked}
          previous={previous.booked}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiTile
          label="Collected (net)"
          value={formatCurrency(current.collected)}
          current={current.collected}
          previous={previous.collected}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiTile
          label="Orders"
          value={formatNumber(current.orders)}
          current={current.orders}
          previous={previous.orders}
          hint={`${formatNumber(
            current.package_orders
          )} packages · ${formatNumber(current.condo_orders)} condos`}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiTile
          label="Average order"
          value={formatCurrency(current.avg_order_value)}
          current={current.avg_order_value}
          previous={previous.avg_order_value}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiTile
          label="Nights booked"
          value={formatNumber(current.nights)}
          current={current.nights}
          previous={previous.nights}
          hint={
            current.avg_lead_time_days !== null
              ? `Booked ${current.avg_lead_time_days} days ahead on average`
              : undefined
          }
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiTile
          label="Refunded"
          value={formatCurrency(current.refunded)}
          current={current.refunded}
          previous={previous.refunded}
          invert
          hint={`${formatNumber(
            current.cancelled_orders
          )} cancelled (${formatCurrency(current.cancelled_value)})`}
        />
      </Grid>

      {/* Sales */}
      <Grid item xs={12}>
        <SalesTrendChart
          data={data.sales_over_time}
          granularity={dataRange.granularity}
        />
      </Grid>

      {/* Comparisons */}
      <SectionTitle subtitle="Orders placed in the selected period, ranked by booked revenue">
        Performance by property and package
      </SectionTitle>
      <Grid item xs={12} lg={6}>
        <BreakdownCard
          title="Hotels & condos"
          rows={data.by_property}
          nameLabel="Property"
          extraColumn={{
            label: "Type",
            render: (row) =>
              row.property_type === "condo" ? "Condo" : "Hotel",
          }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <BreakdownCard
          title="Packages"
          subtitle={
            type === "condo"
              ? "Packages don't apply to condo orders"
              : undefined
          }
          rows={data.by_package}
          nameLabel="Package"
        />
      </Grid>

      {/* Customers */}
      <SectionTitle subtitle="Who books the most, how long they stay, and when">
        Customers & trends
      </SectionTitle>
      <Grid item xs={12}>
        <CustomersSection customers={data.customers} summary={summary} />
      </Grid>

      {/* Payouts */}
      <SectionTitle subtitle="Transfers to hotels and condo owners in the selected period">
        Payouts
      </SectionTitle>
      <Grid item xs={12}>
        <PayoutsSection
          payouts={data.payouts}
          granularity={dataRange.granularity}
        />
      </Grid>

      {/* Upcoming */}
      <SectionTitle
        subtitle={`Looking ahead from today, ${formatDate(dataRange.today)}`}
      >
        Upcoming stays
      </SectionTitle>
      <Grid item xs={12}>
        <UpcomingArrivals upcoming={data.upcoming} />
      </Grid>
    </Grid>
  );
};

const Home = ({ location }) => {
  const { userPermission } = useSelector((state) => state.auth);
  const canSeeSales = !!(userPermission.sales && userPermission.sales.read);

  // Local State
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    if (location && location.state) {
      setUnauthorized(location.state.unauthorized);
    }
  }, [location]);

  return (
    <>
      {canSeeSales ? (
        <SalesDashboard />
      ) : (
        <Paper style={{ padding: 24 }}>
          <Typography component="h1" variant="h4" gutterBottom>
            Welcome
          </Typography>
          <Typography color="textSecondary">
            Sales stats are only shown to users with access to Sales.
          </Typography>
        </Paper>
      )}
      <Snackbar
        open={unauthorized}
        autoHideDuration={6000}
        onClose={() => setUnauthorized(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setUnauthorized(false)}
          severity="warning"
        >
          You don't have the necessary permissions to access that page!
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default withRouter(Home);
